import React from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Stack,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InventoryIcon from '@mui/icons-material/Inventory';
import { BranchOffice } from '@api/BranchOffices/BranchOffice';
import { useNavigate } from 'react-router-dom';

interface ModalViewBranchOfficeProps {
    modalOpen: boolean;
    onClose: () => void;
    branchOffice: BranchOffice | undefined;
}

export const ModalViewBranchOffice: React.FunctionComponent<ModalViewBranchOfficeProps> = ({
    modalOpen,
    onClose,
    branchOffice
}) => {
    const navigation = useNavigate();

    const onViewInventory = () => {
        onClose();
        if (branchOffice?.id) {
            navigation('inventory', {
                state: {
                    branchOfficeId: branchOffice.id,
                    distributionCenterId: branchOffice.distributionCenterId
                }
            });
        }
    };

    if (!branchOffice) {
        return null;
    }

    return (
        <Dialog
            PaperProps={{
                style: { borderRadius: 15, padding: 4 }
            }}
            open={modalOpen}
            onClose={onClose}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={600}>
                        Detalles de la Sucursal
                    </Typography>
                    <IconButton color="secondary" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={3}>
                    <Stack>
                        <Typography variant="h5" fontWeight="600">
                            {branchOffice.name}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {branchOffice.phone}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {branchOffice.address}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mt={1}>
                            Estado: {branchOffice.status ? 'Activo' : 'Inactivo'}
                        </Typography>
                    </Stack>

                    <Box>
                        <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                            Información del Responsable
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <Typography variant="body2" color="text.secondary">
                                    Nombre
                                </Typography>
                                <Typography variant="body1" fontWeight={500}>
                                    {branchOffice.manager?.name || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography variant="body2" color="text.secondary">
                                    Teléfono
                                </Typography>
                                <Typography variant="body1" fontWeight={500}>
                                    {branchOffice.manager?.phone || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography variant="body2" color="text.secondary">
                                    Correo
                                </Typography>
                                <Typography variant="body1" fontWeight={500}>
                                    {branchOffice.manager?.email || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                    Notificación de nuevos pedidos por WhatsApp
                                </Typography>
                                <Typography
                                    variant="body1"
                                    fontWeight={500}
                                    color={
                                        branchOffice.notifyManagerOnNewOrder === false
                                            ? 'error.main'
                                            : 'success.main'
                                    }
                                >
                                    {branchOffice.notifyManagerOnNewOrder === false ? 'Desactivada' : 'Activada'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    <Box>
                        <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                            Servicios
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                    Pickup
                                </Typography>
                                <Typography
                                    variant="body1"
                                    fontWeight={500}
                                    color={branchOffice.hasPickup ? 'success.main' : 'error.main'}
                                >
                                    {branchOffice.hasPickup ? 'Habilitado' : 'Deshabilitado'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                    Delivery
                                </Typography>
                                <Typography
                                    variant="body1"
                                    fontWeight={500}
                                    color={branchOffice.hasDelivery ? 'success.main' : 'error.main'}
                                >
                                    {branchOffice.hasDelivery ? 'Habilitado' : 'Deshabilitado'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 4, pb: 4 }}>
                <Button variant="outlined" color="primary" startIcon={<InventoryIcon />} onClick={onViewInventory}>
                    Ver inventario
                </Button>
            </DialogActions>
        </Dialog>
    );
};
